import { ApiError, onearlyRequest } from '@echo/api-client/client';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';
import type { AuthProvider, IdentifierChannel, LoginFlow } from './session';

/**
 * Where a new account's data is kept, chosen at sign-up (platform docs/architecture/multi-region:
 * identity.md 5.1, implementation/clients.md 1.2). Core lists the regions open for sign-up in
 * this environment and suggests one from the country the edge sees; then the device's locale,
 * its time zone, and Australia. The code screen says where the data will be stored, and tucks
 * the others away behind "Wrong region?" (decided 2026-10-09, Jordan). A region echo knows is
 * said in its own words, translated; any other by Core's label. The codes are Core's (`au`,
 * `us`); nothing else in the app builds or reads a region (rule 6), and a person never sees one.
 */
export interface SignUpRegions {
  /** Open for sign-up in this environment, in Core's order. */
  regions: { code: string; label: string }[];
  /** Core's guess from the country the request came from, when it has one. */
  suggestedRegion: string | undefined;
}

const regionsSchema = z.object({
  regions: z.array(z.object({ code: z.string().min(1), label: z.string().min(1) })),
  suggestedRegion: z.string().nullish(),
});

/**
 * `GET /api/v1/regions`, before anyone is signed in. Null only where Core doesn't serve it yet
 * (it is new with the second region): such a Core answers an unknown path with no bearer 401,
 * checked live on 2026-10-09, or 404. Sign-in then works as before, with no region sent or
 * shown. Anything else that isn't a readable list (the network, a 5xx, an empty or garbled
 * answer) throws, so a new account waits for the list rather than going on without being shown.
 */
export async function loadSignUpRegions(): Promise<SignUpRegions | null> {
  let data: unknown;
  try {
    data = (await onearlyRequest<{ data: unknown }>('/api/v1/regions', { method: 'GET' })).data;
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 404)) return null;
    throw error;
  }
  const parsed = regionsSchema.safeParse(data);
  if (!parsed.success || parsed.data.regions.length === 0) {
    throw new Error('OnEarly: the regions open for sign-up came back unreadable');
  }
  return {
    regions: parsed.data.regions,
    suggestedRegion: parsed.data.suggestedRegion ?? undefined,
  };
}

/** The region a country code names. Only the two regions OnEarly runs map to anything. */
const REGION_OF_COUNTRY: Readonly<Record<string, string>> = { AU: 'au', US: 'us' };

/**
 * The region a BCP 47 tag's region subtag names: `en-AU` → au, `en-US` → us, `zh-Hant-TW` and
 * `en` → none. Extensions (`-u-…`) end the search; `es-419` names an area, not a country.
 */
export function regionOfLocale(tag: string): string | undefined {
  for (const part of tag.replaceAll('_', '-').split('-').slice(1)) {
    if (part.length === 1) return undefined;
    if (/^[A-Za-z]{2}$/.test(part)) return REGION_OF_COUNTRY[part.toUpperCase()];
  }
  return undefined;
}

/** The fifty states' IANA zones (and their old names) outside the prefixes below. */
const US_TIME_ZONES: ReadonlySet<string> = new Set([
  'America/New_York',
  'America/Detroit',
  'America/Chicago',
  'America/Menominee',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Juneau',
  'America/Sitka',
  'America/Metlakatla',
  'America/Yakutat',
  'America/Nome',
  'America/Adak',
  'America/Louisville',
  'America/Indianapolis',
  'America/Fort_Wayne',
  'America/Knox_IN',
  'America/Shiprock',
  'Navajo',
  'Pacific/Honolulu',
]);

/** The region an IANA time zone lies in: Australia's or the United States'. */
export function regionOfTimeZone(zone: string): string | undefined {
  if (zone.startsWith('Australia/') || zone === 'Antarctica/Macquarie') return 'au';
  if (
    US_TIME_ZONES.has(zone) ||
    zone.startsWith('US/') ||
    zone.startsWith('America/Indiana/') ||
    zone.startsWith('America/Kentucky/') ||
    zone.startsWith('America/North_Dakota/')
  ) {
    return 'us';
  }
  return undefined;
}

/**
 * The pre-selected region, in the order decided on 2026-10-08: Core's suggestion, the device's
 * own locale (its region subtag), its time zone, then Australia — the first that is open here.
 * Undefined when nothing is open.
 */
export function defaultSignUpRegion(input: {
  open: SignUpRegions;
  locale: string | undefined;
  timeZone: string | undefined;
}): string | undefined {
  const open = input.open.regions.map((region) => region.code);
  const candidates = [
    input.open.suggestedRegion,
    input.locale ? regionOfLocale(input.locale) : undefined,
    input.timeZone ? regionOfTimeZone(input.timeZone) : undefined,
    'au',
  ];
  return candidates.find((code) => code !== undefined && open.includes(code)) ?? open[0];
}

/** A new account, or one with no home yet (an account an invitation made). */
function needsHome(flow: LoginFlow): boolean {
  return flow.flowKind === 'registration' || flow.homeRegionRequired === true;
}

/**
 * Whether the code screen says where the account's data will be kept: for a new account, or one
 * that has no home yet, wherever Core lists a region open for sign-up, one alone included. A
 * returning person is signed in, and sees nothing of regions.
 */
export function showsHomeRegion(flow: LoginFlow, open: SignUpRegions | null | undefined): boolean {
  return needsHome(flow) && (open?.regions.length ?? 0) > 0;
}

/** The regions open here and the one to pre-select (`useSignUpRegions`). */
export interface SignUpRegionChoice {
  /** Null where Core doesn't list regions: nothing is sent and nothing shown. */
  open: SignUpRegions | null;
  defaultRegion: string | undefined;
}

/**
 * Continue on the sign-in screen. `/login/start` goes out with no region. For an account with no
 * home yet, the flow then takes the pre-selected region as `homeRegion`, which rides it to
 * `/login/verify`, where Core records it (core#721), and `regions` lists what is open, so the
 * code screen can say where the data will be stored and offer the others. Anyone else goes
 * straight on, and costs no regions call. A new account must be shown where its data goes, so
 * it waits for the list: this throws if Core can't give it.
 */
export async function startSignIn(input: {
  provider: Pick<AuthProvider, 'startLogin'>;
  resolveRegions: () => Promise<SignUpRegionChoice>;
  channel: IdentifierChannel;
  identifier: string;
}): Promise<{ flow: LoginFlow; regions?: SignUpRegions }> {
  const flow = await input.provider.startLogin({
    channel: input.channel,
    identifier: input.identifier,
  });
  // A password sign-in (the store reviewers' accounts) is an account that exists, and
  // `/login/password` records no region: nothing to show.
  if (flow.authMethod === 'password' || !needsHome(flow)) return { flow };
  const { open, defaultRegion } = await input.resolveRegions();
  if (!open || !defaultRegion || !showsHomeRegion(flow, open)) return { flow };
  return { flow: { ...flow, homeRegion: defaultRegion }, regions: open };
}

/** Another region picked under "Wrong region?": it rides on the flow to `/login/verify`. */
export function chooseHomeRegion(flow: LoginFlow, region: string): LoginFlow {
  return { ...flow, homeRegion: region };
}

/**
 * The line for each region echo knows, as a whole sentence, so each language owns its article
 * and preposition ("in the United States", "nos Estados Unidos").
 */
const STORED_IN = new Map<string, MessageDescriptor>([
  ['au', msg`Your data will be stored in Australia.`],
  ['us', msg`Your data will be stored in the United States.`],
]);

/** echo's names for the regions it knows: the choices under "Wrong region?". */
const REGION_NAMES = new Map<string, MessageDescriptor>([
  ['au', msg`Australia`],
  ['us', msg`United States`],
]);

/**
 * What the code screen says about where the data will be stored: a region echo knows in its
 * own sentence, any other with Core's label in a template; never the code. Undefined for a
 * region that isn't open.
 */
export function storedInMessage(
  open: SignUpRegions,
  code: string | undefined,
): MessageDescriptor | undefined {
  const region = open.regions.find((candidate) => candidate.code === code);
  if (!region) return undefined;
  const storedIn = region.label;
  return STORED_IN.get(region.code) ?? msg`Your data will be stored in ${storedIn}.`;
}

/** echo's name for a region it knows, to translate; undefined for another, shown by its label. */
export function regionName(code: string): MessageDescriptor | undefined {
  return REGION_NAMES.get(code);
}

/** What "Store my data in a different region" offers: the open regions but the one chosen. */
export function otherRegions(
  open: SignUpRegions,
  code: string | undefined,
): SignUpRegions['regions'] {
  return open.regions.filter((region) => region.code !== code);
}
